import React, { useEffect, useMemo, useRef, useState } from 'react';
import { upvoteEvent, hasUserUpvoted } from '../services/api';
import useMetaTags from '../hooks/useMetaTags';
import { useEvents } from '../hooks/useEvents';
import { useFavorites } from '../hooks/useFavorites';
import { useToast } from '../context/ToastContext';
import { SEO_CONFIG } from '../config/seo.config';
import EventFilters from '../components/EventFilters';
import EventList from '../components/EventList';
import LiveCarousel from '../components/LiveCarousel';
import SkeletonCard from '../components/SkeletonCard';
import { buildHomeListEvents, SportFilter, StatusFilter } from '../utils/eventUtils';
import styles from './Home.module.css';

const Home: React.FC = () => {
    const { events, loading, refreshing, error, lastUpdated, refresh, updateEvent } = useEvents(true);
    const { favoriteIds, toggleFavorite, isFavorite, favoriteCount } = useFavorites();
    const { showToast } = useToast();

    const [sportTab, setSportTab] = useState<SportFilter>('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const defaultFilterApplied = useRef(false);

    useMetaTags({
        title: SEO_CONFIG.pages.home.title,
        description: SEO_CONFIG.pages.home.description,
        keywords: SEO_CONFIG.pages.home.keywords,
        url: 'https://bdlink.live/',
        canonicalUrl: 'https://bdlink.live/',
        image: SEO_CONFIG.global.image,
        locale: 'bn_BD',
        robots: 'index, follow',
    });

    const liveEvents = useMemo(() => events.filter((e) => e.status === 'live'), [events]);
    const scheduledEvents = useMemo(() => events.filter((e) => e.status === 'scheduled'), [events]);
    const showLiveCarousel = !loading && liveEvents.length > 0;

    const cricketCount = events.filter((e) => e.category === 'Cricket').length;
    const footballCount = events.filter((e) => e.category === 'Football').length;

    useEffect(() => {
        if (defaultFilterApplied.current || loading) return;
        if (events.length === 0) return;
        defaultFilterApplied.current = true;
        if (liveEvents.length > 0 && statusFilter === 'all') {
            setStatusFilter('scheduled');
        }
    }, [loading, events.length, liveEvents.length, statusFilter]);

    const listEvents = useMemo(
        () =>
            buildHomeListEvents(events, {
                sportTab,
                statusFilter,
                searchQuery,
                favoritesOnly,
                favoriteIds,
                liveCarouselVisible: showLiveCarousel,
            }),
        [events, sportTab, statusFilter, searchQuery, favoritesOnly, favoriteIds, showLiveCarousel]
    );

    const handleUpvote = (e: React.MouseEvent, eventId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasUserUpvoted(eventId)) {
            showToast('আপনি ইতিমধ্যে আপভোট করেছেন', 'info');
            return;
        }
        const newUpvotes = upvoteEvent(eventId);
        updateEvent(eventId, { upvotes: newUpvotes });
        showToast('ধন্যবাদ! আপভোট যোগ হয়েছে', 'success');
    };

    const handleFavorite = (e: React.MouseEvent, eventId: string) => {
        e.preventDefault();
        e.stopPropagation();
        const wasFavorite = isFavorite(eventId);
        toggleFavorite(eventId);
        showToast(wasFavorite ? 'পছন্দ থেকে সরানো হয়েছে' : 'পছন্দে যোগ হয়েছে', 'success');
    };

    const handleFavoritesToggle = () => {
        if (!favoritesOnly && favoriteCount === 0) {
            showToast('প্রথমে কোনো ম্যাচে ★ চাপুন', 'info');
        }
        setFavoritesOnly((v) => !v);
    };

    const resetFilters = () => {
        setSportTab('all');
        setStatusFilter(showLiveCarousel ? 'scheduled' : 'all');
        setSearchQuery('');
        setFavoritesOnly(false);
    };

    return (
        <div className={styles.home}>
            <header className={styles.broadcastBar} aria-label="লাইভ স্পোর্টস">
                <div className={styles.broadcastInner}>
                    <div className={styles.brandMark}>
                        <span className={styles.brandLive}>LIVE</span>
                        <span className={styles.brandName}>BDLINK</span>
                    </div>
                    <h1 className={styles.headline}>লাইভ ক্রিকেট ও ফুটবল</h1>
                    <div className={styles.ticker} aria-label="ম্যাচ পরিসংখ্যান">
                        <span className={styles.tickerItem}>
                            <strong>{liveEvents.length}</strong> লাইভ
                        </span>
                        <span className={styles.tickerDivider} aria-hidden="true" />
                        <span className={styles.tickerItem}>
                            <strong>{scheduledEvents.length}</strong> আসন্ন
                        </span>
                        <span className={styles.tickerDivider} aria-hidden="true" />
                        <span className={styles.tickerItem}>
                            <strong>{events.length}</strong> মোট
                        </span>
                    </div>
                    {lastUpdated && (
                        <p className={styles.updated}>
                            আপডেট {lastUpdated.toLocaleTimeString('bn-BD', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                    )}
                </div>
            </header>

            {error && (
                <div className={styles.error} role="alert">
                    <span>⚠️</span> {error}
                    <button type="button" onClick={refresh} className={styles.retryBtn}>
                        আবার চেষ্টা করুন
                    </button>
                </div>
            )}

            <div className={styles.container} id="live">
                {showLiveCarousel && (
                    <LiveCarousel
                        events={liveEvents}
                        isFavorite={isFavorite}
                        hasUpvoted={hasUserUpvoted}
                        onUpvote={handleUpvote}
                        onToggleFavorite={handleFavorite}
                    />
                )}

                <div className={styles.stickyFilters}>
                    <EventFilters
                        sportTab={sportTab}
                        statusFilter={statusFilter}
                        searchQuery={searchQuery}
                        favoritesOnly={favoritesOnly}
                        favoriteCount={favoriteCount}
                        resultCount={listEvents.length}
                        cricketCount={cricketCount}
                        footballCount={footballCount}
                        onSportChange={setSportTab}
                        onStatusChange={setStatusFilter}
                        onSearchChange={setSearchQuery}
                        onFavoritesToggle={handleFavoritesToggle}
                        onRefresh={refresh}
                        refreshing={refreshing}
                    />
                </div>

                {loading ? (
                    <div className={styles.list} aria-busy="true">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                ) : listEvents.length === 0 ? (
                    <div className={styles.empty}>
                        <span className={styles.emptyIcon}>📭</span>
                        <h3>কোন ম্যাচ পাওয়া যায়নি</h3>
                        <p>
                            {favoritesOnly
                                ? 'পছন্দের ম্যাচে ★ চাপুন, অথবা ফিল্টার পরিবর্তন করুন'
                                : 'ফিল্টার পরিবর্তন করুন বা রিফ্রেশ করুন'}
                        </p>
                        <button type="button" className={styles.resetBtn} onClick={resetFilters}>
                            সব ম্যাচ দেখুন
                        </button>
                    </div>
                ) : (
                    <EventList
                        events={listEvents}
                        isFavorite={isFavorite}
                        hasUpvoted={hasUserUpvoted}
                        onUpvote={handleUpvote}
                        onToggleFavorite={handleFavorite}
                        groupByStatus={statusFilter === 'all'}
                        hideCompletedDefault={statusFilter === 'all'}
                    />
                )}
            </div>

            <section className={styles.about} id="about">
                <div className={styles.aboutInner}>
                    <h2>BDLINK LIVE</h2>
                    <p>বাংলাদেশের জন্য বিনামূল্যে লাইভ ক্রিকেট ও ফুটবল — মোবাইলে দ্রুত, সহজ, নির্ভরযোগ্য।</p>
                </div>
            </section>
        </div>
    );
};

export default Home;
